import React, {
  ComponentType,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import Button from 'pages/Dashboard/components/Button';
import Dialog from 'pages/Dashboard/components/Dialog';
import {
  dataUrlToFile,
  normalizeToDataUrl,
} from 'utils/files';
import {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import {
  notEmptyString,
} from '@populateui/utils';
import CaptureStep from 'views/EMR/components/WebcamScanDialog/CaptureStep';
import {
  WebcamContext,
  WebcamContextProps,
} from 'views/EMR/components/WebcamScanDialog/webcamContext';
import PreviewStep from 'views/EMR/components/WebcamScanDialog/PreviewStep';
import {
  WebcamScanDialogProps,
} from 'views/EMR/components/WebcamScanDialog';
import convertImageSourcesToPdf from 'views/EMR/components/WebcamScanDialog/utils';

type Props = WebcamScanDialogProps & {
  onClose: () => void;
};

export type WebcamScanStep = 'capture' | 'preview';

const ComponentMap: Record<WebcamScanStep, ComponentType<any>> = {
  capture: CaptureStep,
  preview: PreviewStep,
};

export default function WebcamScanContainer({
  onCapturedFilesConfirmed: _handleUse,
  captureType = 'camera-capture',
  limit = 10,
  multiple = true,
  onClose,
}: Props) {
  const {
    cameraError,
    setCameraError,
    isCameraLoading,
    setIsCameraLoading,
    photoCaptures,
    setPhotoCaptures,
    outputFormat,
    webcamScanStep,
    setWebcamScanStep,
  } = useContext<WebcamContextProps>(WebcamContext);

  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const closeDialog = useCallback(() => {
    setPhotoCaptures([]);
    setCameraError(null);
    setIsCameraLoading(true);
    onClose();
  }, []);

  const dataURLToFile = useCallback((
    dataUrl: string,
    fileName: string,
    defaultMime?: string,
  ): File => dataUrlToFile(dataUrl, fileName, defaultMime), []);

  const handleUsePhoto = useCallback(async () => {
    const result = [];
    setIsProcessing(true);
    if (outputFormat === 'pdf') {
      const normalized = await Promise.all((photoCaptures ?? []).map((u) => normalizeToDataUrl(u)));
      const pdfUrl = await convertImageSourcesToPdf(normalized);
      result.push(pdfUrl);
    } else {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const normalized = await Promise.all((photoCaptures ?? []).map((u) => normalizeToDataUrl(u)));
      const fileLocalUrls: FileLocalUrl[] = normalized.map((dataUrl, idx) => ({
        dataUrl,
        file: dataURLToFile(dataUrl, `${captureType}-${timestamp}-${idx + 1}.jpeg`),
      }));
      result.push(...fileLocalUrls);
    }
    Promise.resolve(_handleUse?.(result));
    setIsProcessing(false);
    closeDialog();
  }, [photoCaptures, dataURLToFile, _handleUse, captureType, outputFormat]);

  const CurrentComponent = useMemo(
    () => ComponentMap[webcamScanStep] ?? CaptureStep,
    [webcamScanStep, ComponentMap],
  );

  return (
    <Dialog
      open
      onClose={closeDialog}
      title="Capture documents / images"
      maxWidth="xl"
      fullWidth={false}
      disableEnforceFocus
      className="overflow-y-hidden overflow-x-hidden !space-y-0 gap-2 !w-[62rem]"
    >
      <div className="flex flex-col gap-4 max-h-[calc(90vh-2rem)]">
        <div className="flex-1 min-h-0 overflow-auto">
          {notEmptyString(cameraError) && (
          <div className="text-red-600 text-sm mb-2">
            {cameraError}
          </div>
          )}
          <div className="flex flex-col md:flex-row gap-6 min-w-0">
            <div className="flex-1 flex flex-col gap-3 items-stretch min-w-0">
              <CurrentComponent />
            </div>
          </div>
        </div>
        <div className="flex gap-3 justify-end border-t border-gray-200 pt-3">
          <Button
            variant="outlined"
            color="inherit"
            onClick={closeDialog}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (webcamScanStep === 'capture') {
                setWebcamScanStep('preview');
              } else {
                handleUsePhoto();
              }
            }}
            disabled={isCameraLoading || photoCaptures.length === 0 || isProcessing}
          >
            {webcamScanStep === 'capture' ? 'Preview →' : 'Confirm'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
